import { useState } from 'react';
import { Plus, Edit2, Trash2, Database, Download } from 'lucide-react';
import { useLabReferenceValues, useCreateLabReferenceValue, useUpdateLabReferenceValue, useDeleteLabReferenceValue, useSeedCanineReferenceValues } from '@/hooks/useLab';
import { LabReferenceValue, LabTestType } from '@/types';
import { LabReferenceValueCreate } from '@/services/lab';

const testTypeLabels: Record<LabTestType, string> = {
  hemograma: 'Hemograma',
  bioquimica: 'Bioquimica',
  orina: 'Orina',
  coprologia: 'Coprologia',
  otros: 'Otros',
};

export default function ReferenceValues() {
  const [speciesFilter, setSpeciesFilter] = useState('');
  const [testTypeFilter, setTestTypeFilter] = useState<LabTestType | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [editingRef, setEditingRef] = useState<LabReferenceValue | null>(null);
  const [deletingRefId, setDeletingRefId] = useState<number | null>(null);
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [formData, setFormData] = useState<LabReferenceValueCreate>({
    test_type: 'hemograma',
    parameter_code: '',
    parameter_name: '',
    species: 'canino',
    reference_min: undefined,
    reference_max: undefined,
    reference_text: '',
    unit: '',
    display_order: 0,
  });

  const { data: referenceValues = [], isLoading, refetch } = useLabReferenceValues({
    species: speciesFilter || undefined,
    test_type: testTypeFilter || undefined,
  });
  const createMutation = useCreateLabReferenceValue();
  const updateMutation = useUpdateLabReferenceValue();
  const deleteMutation = useDeleteLabReferenceValue();
  const seedMutation = useSeedCanineReferenceValues();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRef) {
        await updateMutation.mutateAsync({
          id: editingRef.id,
          data: {
            reference_min: formData.reference_min,
            reference_max: formData.reference_max,
            reference_text: formData.reference_text,
            unit: formData.unit,
            display_order: formData.display_order,
          },
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (ref: LabReferenceValue) => {
    setEditingRef(ref);
    setFormData({
      test_type: ref.test_type,
      parameter_code: ref.parameter_code,
      parameter_name: ref.parameter_name,
      species: ref.species,
      reference_min: ref.reference_min ?? undefined,
      reference_max: ref.reference_max ?? undefined,
      reference_text: ref.reference_text || '',
      unit: ref.unit || '',
      display_order: ref.display_order,
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deletingRefId) return;
    await deleteMutation.mutateAsync(deletingRefId);
    setDeletingRefId(null);
    refetch();
  };

  const handleSeedCanine = async () => {
    try {
      await seedMutation.mutateAsync();
      setShowSeedConfirm(false);
      refetch();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRef(null);
    setFormData({
      test_type: 'hemograma',
      parameter_code: '',
      parameter_name: '',
      species: 'canino',
      reference_min: undefined,
      reference_max: undefined,
      reference_text: '',
      unit: '',
      display_order: 0,
    });
  };

  // Group by test type
  const groupedValues = referenceValues.reduce((acc, ref) => {
    if (!acc[ref.test_type]) {
      acc[ref.test_type] = [];
    }
    acc[ref.test_type].push(ref);
    return acc;
  }, {} as Record<LabTestType, LabReferenceValue[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Valores de Referencia</h1>
          <p className="text-gray-600">Gestionar valores de referencia para analisis</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowSeedConfirm(true)}
            disabled={seedMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-lab-600 text-lab-600 rounded-lg hover:bg-lab-50 disabled:opacity-50"
          >
            <Download className="h-5 w-5" />
            Cargar Caninos
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-lab-600 text-white px-4 py-2 rounded-lg hover:bg-lab-700"
          >
            <Plus className="h-5 w-5" />
            Nuevo Valor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4">
          <select
            value={speciesFilter}
            onChange={(e) => setSpeciesFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
          >
            <option value="">Todas las especies</option>
            <option value="canino">Canino</option>
            <option value="felino">Felino</option>
            <option value="equino">Equino</option>
            <option value="bovino">Bovino</option>
          </select>
          <select
            value={testTypeFilter}
            onChange={(e) => setTestTypeFilter(e.target.value as LabTestType | '')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
          >
            <option value="">Todos los tipos</option>
            {Object.entries(testTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : referenceValues.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hay valores de referencia</p>
            <p className="text-sm mt-2">Usa el boton "Cargar Caninos" para cargar valores predefinidos</p>
          </div>
        ) : (
          <div className="divide-y">
            {Object.entries(groupedValues).map(([testType, values]) => (
              <div key={testType} className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 uppercase text-sm">
                  {testTypeLabels[testType as LabTestType]}
                </h3>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Codigo</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Parametro</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Especie</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Ref. Min</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Ref. Max</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Unidad</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {values.map((ref) => (
                      <tr key={ref.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-sm">{ref.parameter_code}</td>
                        <td className="px-4 py-2 font-medium">{ref.parameter_name}</td>
                        <td className="px-4 py-2 capitalize">{ref.species}</td>
                        <td className="px-4 py-2">{ref.reference_min ?? '-'}</td>
                        <td className="px-4 py-2">{ref.reference_max ?? '-'}</td>
                        <td className="px-4 py-2">{ref.unit || '-'}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(ref)}
                              className="p-1 text-lab-600 hover:bg-lab-50 rounded"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeletingRefId(ref.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingRefId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Valor</h3>
              <p className="text-gray-600">¿Estás seguro de que querés eliminar este valor de referencia?</p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setDeletingRefId(null)} className="flex-1 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancelar</button>
              <button onClick={handleDelete} disabled={deleteMutation.isPending} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seed Canine Confirmation Modal */}
      {showSeedConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 bg-lab-100 rounded-full flex items-center justify-center mb-4">
                <Download className="h-7 w-7 text-lab-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cargar Valores Caninos</h3>
              <p className="text-gray-600">Se cargarán los valores de referencia predefinidos para caninos (Bioquímica, Hemograma, Orina).</p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowSeedConfirm(false)} className="flex-1 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancelar</button>
              <button onClick={handleSeedCanine} disabled={seedMutation.isPending} className="flex-1 px-4 py-2.5 bg-lab-600 text-white rounded-lg hover:bg-lab-700 font-medium disabled:opacity-50">
                {seedMutation.isPending ? 'Cargando...' : 'Cargar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingRef ? 'Editar Valor de Referencia' : 'Nuevo Valor de Referencia'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de analisis *</label>
                  <select
                    required
                    disabled={!!editingRef}
                    value={formData.test_type}
                    onChange={(e) => setFormData({ ...formData, test_type: e.target.value as LabTestType })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500 disabled:bg-gray-100"
                  >
                    {Object.entries(testTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Especie *</label>
                  <select
                    required
                    disabled={!!editingRef}
                    value={formData.species}
                    onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500 disabled:bg-gray-100"
                  >
                    <option value="canino">Canino</option>
                    <option value="felino">Felino</option>
                    <option value="equino">Equino</option>
                    <option value="bovino">Bovino</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codigo *</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingRef}
                    value={formData.parameter_code}
                    onChange={(e) => setFormData({ ...formData, parameter_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500 disabled:bg-gray-100"
                    placeholder="ej: HEMOGLOBINA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingRef}
                    value={formData.parameter_name}
                    onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500 disabled:bg-gray-100"
                    placeholder="ej: Hemoglobina"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ref. Min</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.reference_min ?? ''}
                    onChange={(e) => setFormData({ ...formData, reference_min: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ref. Max</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.reference_max ?? ''}
                    onChange={(e) => setFormData({ ...formData, reference_max: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="ej: g/dl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto de referencia</label>
                <input
                  type="text"
                  value={formData.reference_text}
                  onChange={(e) => setFormData({ ...formData, reference_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="ej: Negativo, Positivo, etc."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-lab-600 text-white rounded-lg hover:bg-lab-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
